import { ageTextConflicts, itemUseConflict, itemUseIgnore, traitTextConflicts, type AgeConflict, type IgnoredTerm, type ItemUseConflict, type SpanRole, type StoryCtx, type StoryNode, type TextRef, type TraitConflict } from '@dispositio/core';
import type { Mark, Node as PMNode } from '@tiptap/pm/model';
import { PluginKey } from '@tiptap/pm/state';
import { suggestExtension, type SuggestLive, type SuggestState } from './suggest';

/**
 * Was der Text über eine Figur sagt und zu der Zeit nicht stimmt (`suggest.ts`, Art `conflict`), rot an der Stelle:
 * - Alter (AP-40, core `ageText`): „der zwölfjährige Max“ – laut Geburt ist er anders alt; der weitere Ausweg zeigt die Geburt.
 * - Merkmal (AP-42, core `continuity`): „die blonden Haare“ – seine Haare sind zu der Zeit grau; der weitere Ausweg zeigt die Szene, seit
 *   der es gilt.
 * - Benutzter Gegenstand (AP-42): eine Handlung der Figur mit einem Gegenstand, der zu der Zeit bei jemand anderem, verloren oder an einem
 *   anderen Ort ist; der weitere Ausweg zeigt, seit wann.
 * ✕ merkt jeweils „ist Absicht“ im Dokument. Nur im Draft einer Szene.
 */

export type FactHit =
  | { kind: 'age'; from: number; to: number; conflict: AgeConflict }
  | { kind: 'trait'; from: number; to: number; conflict: TraitConflict }
  | { kind: 'item'; from: number; to: number; conflict: ItemUseConflict };

export interface FactLive extends SuggestLive<FactHit> {
  ctx: StoryCtx;
  scene: StoryNode | null;
  /** Element zu einem Verweisschlüssel */
  entityOf: (key: string) => string | null;
  ignored: readonly IgnoredTerm[] | undefined;
  title: (hit: FactHit) => string;
  other: (hit: FactHit) => { label: string; title: string } | null;
}

export const factConflictKey = new PluginKey<SuggestState<FactHit>>('factConflicts');

/** Je Absatz: sein Text und die verknüpften Wörter darin (mit Schlüssel), wie sie die Erkennung in core liest */
function blockHits(doc: PMNode, l: FactLive, scene: StoryNode): FactHit[] {
  const out: FactHit[] = [];
  doc.descendants((node, pos) => {
    if (!node.isTextblock) return true;
    if (node.type.spec.code) return false;
    const text = node.textBetween(0, node.content.size, '\n', '\n');
    const refs: TextRef[] = [];
    node.forEach((child, offset) => {
      const link = child.isText ? child.marks.find((m) => m.type.name === 'refLink') : undefined;
      if (!link?.attrs.key) return;
      const last = refs[refs.length - 1];
      // ein verknüpftes Wort über mehrere Textstücke (etwa teils kursiv) zählt einmal
      if (last && last.to === offset && last.key === link.attrs.key) last.to = offset + child.nodeSize;
      else refs.push({ from: offset, to: offset + child.nodeSize, key: String(link.attrs.key) });
    });
    if (refs.length === 0) return false;
    for (const c of ageTextConflicts(l.ctx, scene, text, refs, l.ignored)) out.push({ kind: 'age', from: pos + 1 + c.from, to: pos + 1 + c.to, conflict: c });
    for (const c of traitTextConflicts(l.ctx, scene, text, refs, l.ignored)) out.push({ kind: 'trait', from: pos + 1 + c.from, to: pos + 1 + c.to, conflict: c });
    return false;
  });
  return out;
}

/** Handlungen im Dokument (ein Bezug über mehrere Textstücke zählt einmal) mit den Verweisen darin */
function actionHits(doc: PMNode, l: FactLive, scene: StoryNode): FactHit[] {
  const spans: { from: number; to: number; keys: string[]; linked: Set<string> }[] = [];
  doc.descendants((node, pos) => {
    if (!node.isText) return true;
    const span = node.marks.find((m: Mark) => m.type.name === 'refSpan' && (m.attrs.role as SpanRole) === 'handlung');
    if (!span) return false;
    const keys = span.attrs.keys as string[];
    const link = node.marks.find((m: Mark) => m.type.name === 'refLink');
    const last = spans[spans.length - 1];
    const same = last && last.to === pos && last.keys.join(' ') === keys.join(' ');
    const cur = same ? last : { from: pos, to: pos, keys, linked: new Set<string>() };
    cur.to = pos + node.nodeSize;
    if (link?.attrs.key) cur.linked.add(String(link.attrs.key));
    if (!same) spans.push(cur);
    return false;
  });
  const out: FactHit[] = [];
  const ignored = (c: ItemUseConflict): boolean => {
    const entry = itemUseIgnore(l.ctx, c);
    return !!l.ignored?.some((x) => x.term === entry.term && x.before === entry.before && x.after === entry.after);
  };
  for (const s of spans) {
    const ids = [...new Set([...s.keys, ...s.linked].map(l.entityOf).filter((id): id is string => !!id))];
    const hit = ids.flatMap((p) => ids.map((i) => (p === i ? null : itemUseConflict(l.ctx, scene, p, i)))).find((c): c is ItemUseConflict => !!c && !ignored(c));
    if (hit) out.push({ kind: 'item', from: s.from, to: s.to, conflict: hit });
  }
  return out;
}

const factHits = (doc: PMNode, l: FactLive): FactHit[] => (l.scene ? [...blockHits(doc, l, l.scene), ...actionHits(doc, l, l.scene)].sort((a, b) => a.from - b.from) : []);

const wordOf = (h: FactHit): string => (h.kind === 'age' ? String(h.conflict.years) : h.kind === 'trait' ? h.conflict.said : h.conflict.itemId);

export const factConflictMarks = (live: { current: FactLive }) =>
  suggestExtension<FactHit, FactLive>(
    {
      name: 'factConflicts',
      kind: 'conflict',
      key: factConflictKey,
      base: 'rt-conflict',
      hits: factHits,
      mark: (h, l) => ({ class: `rt-fact-conflict ${h.kind}`, 'data-fact-conflict': h.kind, 'data-word': wordOf(h), title: l.title(h) }),
      labels: (_h, l) => ({ accept: null, ignore: l.labels.ignore }),
      other: (h, l) => l.other(h),
      word: wordOf,
      testId: 'fact',
    },
    live,
  );
